<template>
  <div class="media-form story">
    <HeadSection :title="isEditMode ? `Item #${storyId}` : 'Create new story'" permissionOfSection="stories"
      :breadcrumbs="breadcrumbs" :uuid="storyUuid">
      <template #activeSwitch>
        <div class="ml-4">
          <a-switch v-model="form.is_active" />
        </div>
      </template>
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode">
            <a-popconfirm title="Sure to delete?" placement="bottom" @confirm.stop="deleteStory">
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button :loading="loading" type="primary" class="ml-4" @click="update">
              Update
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="mainLoading || userLoading" permissionOfSection="banners">
      <!-- <div style="padding: 24px; background-color: #fff"> -->
      <a-form-model :rules="rules" :model="form" ref="form">
        <div class="media-form__main">
          <span class="font-weight-bold font-size-16 mb-3">General</span>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="name" label="Name (for internal usage)" :colon="false">
                <a-input v-model="form.name" placeholder="Name" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="extId" :colon="false">
                <template #label>External ID</template>
                <a-input v-model="form.extId" placeholder="External id" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="contentType" label="Content type">
                <a-select v-model="form.contentType" size="large" :disabled="loading" allow-clear>
                  <template #placeholder><span style="color: #a1a1c2">Select content type</span></template>
                  <a-select-option v-for="contentType in contentTypeOptions" :key="contentType.value"
                    :value="contentType.value">
                    {{ contentType.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <ActivityPeriods
            ref="activity-periods"
            :periods="storyToEdit?.context.context.periods"
            :showIndex="false"
            :loading="loading"
            buttonPos="bottom"
            showLabel
            useTimezone
          >
            <template #label>
              Show Period
            </template>
            <template #buttonText>
              + Add new period
            </template>
          </ActivityPeriods>
          <a-form-model-item label="Section" prop="sectionType" :colon="false" style="margin-bottom:  8px;">
            <a-radio-group name="radioGroup" v-model="form.sectionType" :default-value="form.sectionType"
              :disabled="loading || isSectionTypeDisabled" @change="clearApps">
              <a-radio value="viewscreens"> Viewscreens </a-radio>
              <a-radio value="screens"> Screens </a-radio>
            </a-radio-group>
          </a-form-model-item>
          <a-row v-if="form.sectionType === 'viewscreens'">
            <a-col :span="6">
              <a-form-model-item label="Viewscreens" prop="selectedViewscreens" :colon="false">
                <a-select v-model="form.selectedViewscreens" size="large" :disabled="loading" mode="multiple" showArrow
                  allow-clear @change="clearApps" show-search :filterOption="(input, option) => option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0">
                  <template #placeholder><span style="color: #a1a1c2;">Select viewscreen</span></template>
                  <a-select-option v-for="viewscreen in viewscreens" :key="viewscreen.id">
                    {{ viewscreen.name }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <div v-else>
            <a-row class="mb-3">
              <a-col :span="6">
                <a-form-model-item label="Screens" prop="sections" :colon="false" class="mb-3">
                  <a-select v-model="form.sections" size="large" mode="multiple" showArrow :disabled="loading"
                    allow-clear>
                    <template #placeholder><span style="color: #a1a1c2;">Select screens</span></template>
                    <a-select-option v-for="section in sections" :key="section.value" :value="section.value">
                      {{ section.label }}
                    </a-select-option>
                  </a-select>
                </a-form-model-item>
              </a-col>
            </a-row>
            <a-row>
              <a-col :span="6">
                <a-form-model-item prop="segments" :colon="false">
                 <template #label>
                    <SegmentsLabel />
                  </template>
                  <div class="d-flex align-items-center w-full">
                   <SegmentsSelect v-model="form.segments" :segments="segments" :loading="loading" @change="validateSegments" />
                  </div>
                </a-form-model-item>
              </a-col>
            </a-row>
          </div>
          <div>
            <a-form-model-item prop="apps">
              <div class="mb-4">
                <div class="d-flex align-items-center">
                  <span class="media-form--title required-label mr-3">Apps</span>
                  <a-tooltip v-if="form.sectionType === 'viewscreens'" slot="suffix"
                    title="Inherited from selected viewscreen">
                    <a-icon type="info-circle" class="text-primary" />
                  </a-tooltip>
                  <a v-else @click.stop="openAddAppModal" class="add-btn">+ Add</a>
                </div>
                <AppsCollapse class="mt-2" v-if="form.apps" :apps="form.apps" :appsShown="4" />
              </div>
            </a-form-model-item>
          </div>
          <a-divider class="m-0 mb-3" />
          <div>
            <div class="d-flex flex-row align-items-center justify-content-between mb-2 position-relative">
              <span class="font-weight-bold font-size-16">Media</span>
              <LanguageSelect
                :value="selectedLanguage"
                :langs="languages"
                :contentFilled="filledLangs"
                :error="formErrorState"
                short-button-label
                :style="{ top: 0 }"
                @copyFromDefault="handleCopyFromDefault"
                @clear="handleClearLang"
                @input="handleLangChange"
              />
            </div>
            <div>
              <a-tabs type="editable-card" :defaultActiveKey="selectedStoryItems[0].id" :active-key="selectedTabId"
                @change="handleTabChange" @edit="openDeleteCoverModal" hideAdd
                :tabBarStyle="{ border: 'none', display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-end' }">
                <template #tabBarExtraContent>
                  <a-tooltip v-if="isAddStoryCoverDisabled" :getPopupContainer="getPopupContainer" placement="top"
                    title="Please, before adding new story cover fill in the previous one">
                    <a class="add-btn ml-2 add-btn__disabled"> + Add</a>
                  </a-tooltip>
                  <a v-else @click="addStoryCover" class="add-btn ml-2" :class="{'add-btn__disabled' : loading}"> +
                    Add</a>
                </template>
                <a-tab-pane v-for="storyItem in selectedStoryItems"
                  :closable="storyItem.type !== 'preview' && selectedStoryItems.length > 2" :key="storyItem.id"
                  :tab="storyItem.type === 'preview' ? 'Preview': storyItem.name" />
              </a-tabs>
              <div v-if="selectedStoryItemType === 'preview'" class="media-form__preview">
                <div class="d-flex flex-column">
                  <span class="mb-2 media-form--title required-label">Preview</span>
                  <div class="preview-container">
                    <div>
                      <a-upload list-type="picture-card" :show-upload-list="false" @change="handlePreviewUpload"
                        :disabled="loading">
                        <div>
                          <div v-if="selectedPreview.url" class="media-form__preview--image">
                            <img :src="selectedPreview.url" />
                          </div>
                          <template v-else>
                            <a-icon :type="selectedPreview.loading ? 'loading' : 'plus'" />
                            <div class="text-gray-7">
                              File size<br />
                              500 x 500 px
                            </div>
                          </template>
                        </div>
                      </a-upload>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="media-form__story">
                <div class="d-flex flex-column">
                  <div class="mb-4">
                    <span class="mb-2 media-form--title required-label">Element</span>
                    <div class="story-container">
                      <div>
                        <a-upload list-type="picture-card" class="media-form__story--uploader" :show-upload-list="false"
                          @change="handleStoryUpload" :disabled="loading">
                          <div v-if="selectedStory">
                            <div v-if="selectedStory.preview" class="media-form__story--image">
                              <a-icon v-if="selectedStory.isRulesAvailable" type="info-circle" class="info-icon"
                                :class="{'icon-dark' : selectedRules.iconType === 'dark', 'icon-light': selectedRules.iconType === 'light'}" />
                              <video v-if="selectedStory.isVideo" autoplay muted loop :src="selectedStory.preview" />
                              <img v-else :src="selectedStory.preview" />
                            </div>
                            <template v-else>
                              <a-icon :type="selectedStory.loading ? 'loading' : 'plus'" />
                              <div class="text-gray-7">
                                File size<br />
                                1400 x 2200px </div>
                            </template>
                          </div>
                        </a-upload>
                      </div>
                    </div>
                  </div>
                  <div v-if="selectedStory" class="d-flex flex-column mb-2">
                    <div class="mb-3">
                      <span class="font-weight-bold font-size-14 mr-3">Redirect</span>
                      <a-switch v-model="selectedStory.isRedirectAvailable" @change="handleRedirectSwitch" />
                    </div>
                    <div v-if="selectedStory.isRedirectAvailable">
                      <div class="d-flex flex-column redirect-list">
                        <div class="">
                          <a-row class="mb-3">
                            <a-col :span="3" class="mr-4">
                              <a-form-model-item :colon="false">
                                <template #label><span class="required-label">Name</span></template>
                                <a-input placeholder="Name" :disabled="loading" v-model="selectedRedirect.buttonName"
                                  size="large">
                                  <template #placeholder><span style="color: #a1a1c2">Name</span></template>
                                </a-input>
                              </a-form-model-item>
                            </a-col>
                            <a-col :span="3" class="mr-4">
                              <a-form-model-item prop="buttonColor" :colon="false">
                                <template #label><span class="required-label">Button color</span></template>
                                <a-input placeholder="#112233" :disabled="loading"
                                  v-model="selectedRedirect.buttonColor" size="large">
                                </a-input>
                              </a-form-model-item>
                            </a-col>
                            <a-col :span="3">
                              <a-form-model-item prop="fontColor" :colon="false">
                                <template #label><span class="required-label">Font color</span></template>
                                <a-input placeholder="#112233" :disabled="loading" v-model="selectedRedirect.fontColor"
                                  size="large">
                                </a-input>
                              </a-form-model-item>
                            </a-col>
                          </a-row>
                          <a-row>
                            <a-col :span="3" class="mr-4">
                              <a-form-model-item :colon="false">
                                <template #label><span class="required-label">Link type</span></template>
                                <a-select v-model="selectedRedirect.linkType"
                                  @change="(value) => handleLinkTypeChange('redirect')" size="large" :disabled="loading"
                                  allow-clear>
                                  <template #placeholder><span style="color: #a1a1c2">Select link type</span></template>
                                  <a-select-option value="custom"> Custom </a-select-option>
                                  <a-select-option value="sport"> Sport </a-select-option>
                                  <a-select-option value="match"> Match </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </a-col>
                            <a-col :span="7">
                              <div v-if="selectedRedirect.linkType === 'custom'">
                                <a-form-model-item :colon="false">
                                  <template #label>
                                    <span class="required-label">Link</span>
                                    <a-button @click="openAddLinkTemplatesModal" :disabled="isLinkModalDisabled"
                                      size="small" type="link"
                                      style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save as a
                                      template</a-button>
                                  </template>
                                  <a-select class="media-form__custom-select" :value="selectedRedirect.link"
                                    :disabled="loading" mode="tags"
                                    @change="(value) => handleTemplateChange(value, 'redirect')" @inputKeydown="(e) => handleLinkTypeChange('redirect', e)"  optionLabelProp="label"
                                    size="large">
                                    <template #placeholder><span
                                        style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                                    <a-select-option v-for="linkTemplate in filteredLinkTemplates"
                                      :key="String(linkTemplate.id)" :value="String(linkTemplate.url)"
                                      :label="linkTemplate.url">
                                      <div class="d-flex justify-content-between align-items-center">
                                        <div class="link-container">
                                          <span class="link-name">{{ linkTemplate.name }}</span>
                                          <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                        </div>
                                        <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                          :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                          @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id)">
                                          <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                        </a-popconfirm>
                                      </div>
                                    </a-select-option>
                                  </a-select>
                                </a-form-model-item>
                              </div>
                              <div v-if="selectedRedirect.linkType === 'match'">
                                <a-form-model-item :colon="false" label="Match link">
                                  <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617"
                                    :disabled="loading" v-model="selectedRedirect.link" size="large" />
                                </a-form-model-item>
                              </div>
                              <div v-if="selectedRedirect.linkType === 'sport'">
                                <a-form-model-item :colon="false" label="Sport">
                                  <a-select v-model="selectedRedirect.sportId" size="large" :disabled="loading"
                                    allow-clear>
                                    <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                                    <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                      {{ sport.name }}
                                    </a-select-option>
                                  </a-select>
                                </a-form-model-item>
                              </div>
                            </a-col>
                          </a-row>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="selectedStory" class="d-flex flex-column">
                    <div class="mb-3">
                      <span class="font-weight-bold font-size-14 mr-3">Rules</span>
                      <a-switch v-model="selectedStory.isRulesAvailable" @change="handleRulesSwitch" disabled />
                    </div>
                    <div v-if="selectedStory.isRulesAvailable" class="mb-4">
                      <div class="d-flex flex-column rules-list">
                        <a-row class="mb-4">
                          <a-col>
                            <a-form-model-item :colon="false">
                              <template #label><span class="required-label">Icon</span></template>
                              <div class="d-flex">
                                <div @click="setIconType('dark')" class="media-form__radio media-form__radio--dark mr-4"
                                  :class="{ 'media-form__radio--selected': selectedRules.iconType === 'dark' }">
                                  <a-icon type="info-circle" />
                                </div>
                                <div @click="setIconType('light')" class="media-form__radio media-form__radio--light"
                                  :class="{ 'media-form__radio--selected': selectedRules.iconType === 'light' }">
                                  <a-icon type="info-circle" />
                                </div>
                              </div>
                            </a-form-model-item>
                          </a-col>
                        </a-row>
                        <a-row class="mb-4">
                          <a-col>
                            <a-radio-group name="radioGroup" v-model="selectedRules.type"
                              :default-value="selectedRules.type" :disabled="loading || isRulesTypeDisabled"
                              @change="handleChangeRulesType">
                              <a-radio value="link"> Link </a-radio>
                              <a-radio value="bottomDialogue"> Bottom dialogue </a-radio>
                            </a-radio-group>
                          </a-col>
                        </a-row>
                        <a-row v-if="selectedRules.type === 'link'">
                          <a-col :span="3" class="mr-4">
                            <a-form-model-item :colon="false">
                              <template #label><span class="required-label">Link type</span></template>
                              <a-select v-model="selectedRules.linkType" size="large" :disabled="loading"
                                @change="(value) => handleLinkTypeChange('rules')" allow-clear>
                                <template #placeholder><span style="color: #a1a1c2">Select link
                                    type</span></template>
                                <a-select-option value="custom"> Custom </a-select-option>
                                <a-select-option value="sport"> Sport </a-select-option>
                                <a-select-option value="match"> Match </a-select-option>
                              </a-select>
                            </a-form-model-item>
                          </a-col>
                          <a-col :span="7">
                            <div v-if="selectedRules.linkType === 'custom'">
                              <a-form-model-item :colon="false">
                                <template #label>
                                  <span class="required-label">Link</span>
                                  <a-button @click="openAddLinkTemplatesModal" :disabled="loading" size="small"
                                    type="link"
                                    style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save as a
                                    template</a-button>
                                </template>
                                <a-select class="media-form__custom-select" :value="selectedRules.link"
                                  :disabled="loading" mode="tags"
                                  @change="(value) => handleTemplateChange(value, 'rules')" @inputKeydown="(e) => handleLinkTypeChange('rules', e)"  optionLabelProp="label"
                                  size="large">
                                  <template #placeholder><span
                                      style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                                  <a-select-option v-for="linkTemplate in filteredLinkTemplates" :key="String(linkTemplate.id)"
                                    :value="String(linkTemplate.url)" :label="linkTemplate.url">
                                    <div class="d-flex justify-content-between align-items-center">
                                      <div class="link-container">
                                        <span class="link-name">{{ linkTemplate.name }}</span>
                                        <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                      </div>
                                      <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                        :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                        @confirm="(e) => deleteTemplate(e, linkTemplate.id)">
                                        <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                      </a-popconfirm>
                                    </div>
                                  </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </div>
                            <div v-if="selectedRules.linkType === 'match'">
                              <a-form-model-item :colon="false" label="Match link">
                                <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617" :disabled="loading"
                                  v-model="selectedRules.link" size="large" />
                              </a-form-model-item>
                            </div>
                            <div v-if="selectedRules.linkType === 'sport'">
                              <a-form-model-item :colon="false" label="Sport">
                                <a-select v-model="selectedRules.sportId" size="large" :disabled="loading" allow-clear>
                                  <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                                  <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                    {{ sport.name }}
                                  </a-select-option>
                                </a-select>
                              </a-form-model-item>
                            </div>
                          </a-col>
                        </a-row>
                        <a-row v-else>
                          <a-col>
                            <a-button @click="openRulesTextModal" type="primary" :disabled="loading">{{
                              selectedRules.rulesQuilText?.text?.length ? '+ Edit text' : '+ Add text' }}</a-button>
                          </a-col>
                        </a-row>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-form-model>
      <div v-if="errors.oneFullStory" style="color: red; margin-top: 8px;">At least one language should have both
        preview and story</div>
      <div v-if="errors.bothPreviewAndStory" style="color: red; margin-top: 8px">Can't save language with preview but
        without story and vice versa</div>
      <div v-if="errors.isRedirectsValid" style="color: red; margin-top: 8px">All redirects should have name, link type
        and link itself</div>
      <div v-if="errors.redirectColorLength" style="color: red; margin-top: 8px">Redirect color must be a valid 7-character hex code (e.g., #FFFFFF)</div>
      <div v-if="errors.isLinkRuleValid" style="color: red; margin-top: 8px">All link rules should have link type and
        link itself</div>
      <div v-if="errors.isDialogueRuleValid" style="color: red; margin-top: 8px">All dialogue rules should have title
        and text</div>
    </MainContainer>
    <a-modal v-model="addAppsModal" title="Add new apps" :footer="null" width="700px" :destroyOnClose="true">
      <AppsModal @selectApps="changeSelectedApps" @close="closeAddAppsModal" :modalAppsData="modalAppsData" />
    </a-modal>
    <a-modal v-model="addLinkTemplateModal" width="395px" :destroyOnClose="true" :footer="null">
      <template #title><span class="modal-title">Add new template</span></template>
      <TemplatesModal :templateLink="selectedRedirect?.link || ''" @addLink="handleAddTemplatesModal"
        @closeTemplateModal="() => addLinkTemplateModal = false" />
    </a-modal>
    <a-modal v-model="addRulesTextModal" title="Add text" :footer="null" width="700px" :destroyOnClose="true">
      <RulesTextModal :rulesObject="selectedRules?.rulesQuilText" @closeModalFunction="closeRulesTextModal"
        @createdRules="handleSaveRules" />
    </a-modal>
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import AppsCollapse from '@/views/apps/media/components/appsCollapse.vue'
import apiClient from '@/services/axios'
import ViewscreenService from '@/services/api/apiViewscreenService'
import MediaService from '@/services/api/apiMediaService'
import ActivityPeriods from '@/components/custom/ActivityPeriods.vue'
import SegmentsSelect from '@/components/custom/segments/segmentsSelect.vue'
import SegmentsLabel from '@/components/custom/segments/segmentsLabel.vue'
import TemplatesModal from '@/components/custom/modals/media/linkTemplates.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import RulesTextModal from '@/views/apps/media/components/stories/form/modals/rulesTextModal.vue'
import { Modal } from 'ant-design-vue'

import { returnToLastList } from '@/services/utils/routerHelpers'
import { DEFAULT_LANG } from '@/constants/lang'
import { STORY_CONTENT_FORM_FIELDS, defaultPreview, defaultCover, defaultStoryRedirect, defaultStoryRules } from '@/constants/media/stories'

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

const isContentEqual = (content, template) => {
  if (!content || !template) return content === template

  // Compare preview (ignoring id)
  const previewEqual =
    content.preview?.url === (template.preview?.url ?? '') &&
    content.preview?.file === (template.preview?.file ?? null) &&
    content.preview?.loading === (template.preview?.loading ?? false)

  if (!previewEqual) return false

  // Compare stories length
  if (content.stories?.length !== template.stories?.length) return false

  // Compare each story (ignoring id and name)
  return content.stories.every((story, index) => {
    const templateStory = template.stories[index]
    return (
      story.preview === (templateStory.preview ?? '') &&
      story.file === (templateStory.file ?? null) &&
      story.isVideo === (templateStory.isVideo ?? false) &&
      story.loading === (templateStory.loading ?? false) &&
      story.isRedirectAvailable === (templateStory.isRedirectAvailable ?? false) &&
      story.isRulesAvailable === (templateStory.isRulesAvailable ?? false)
    )
  })
}

function getBase64(img) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result))
    reader.readAsDataURL(img)
  })
}

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(file)
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  })
}

function getVideoDimensions(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.src = URL.createObjectURL(file)
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      })
    }
  })
}

export default {
  name: 'StoriesForm',
  components: { ActivityPeriods, SegmentsLabel, SegmentsSelect, AppsModal, AppsCollapse, HeadSection, MainContainer, TemplatesModal, RulesTextModal, LanguageSelect },
  data() {
    return {
      storyToEdit: undefined,
      languages: [{
        desc: 'Russian',
        id: 0,
        name: 'ru',
      }],
      langOptions: [''],
      linkTemplates: [],
      sports: [],
      segments: [],
      viewscreens: [],
      form: {
        is_active: true,
        name: '',
        extId: '',
        contentType: undefined,
        selectedViewscreens: [],
        sectionType: 'viewscreens',
        sections: [],
        segments: [],
        apps: [],
      },
      activityPeriods: [],
      contentTypeOptions: [
        { label: 'Partners', value: 'Partners' },
        { label: 'Promotion', value: 'Promotion' },
        { label: 'Editorial', value: 'Editorial' },
        { label: 'SMM', value: 'SMM' },
        { label: 'Product', value: 'Product' },
        { label: 'Special project', value: 'Special project' },
      ],
      tabCounter: 0,
      active: false,
      type: 'image',
      selectedLanguage: 'ru',
      selectedTabId: '1',
      content: {
        ru: {
          ...STORY_CONTENT_FORM_FIELDS,
        },
      },
      loading: false,
      validationResult: true,
      errors: {
        oneFullStory: false,
        bothPreviewAndStory: false,
        isRedirectsValid: false,
        redirectColorLength: false,
        isLinkRuleValid: false,
        isDialogueRuleValid: false,
      },
      isModalOpenOnClick: false,
      sections: [
        { label: 'Live', value: 1 },
        { label: 'Line', value: 2 },
        { label: 'Promo', value: 3 },
        { label: 'Main Web', value: 4 },
        { label: 'Registration', value: 5 },
        { label: 'Side menu', value: 6 },
        { label: 'Bottom menu', value: 7 },
      ],
      addAppsModal: false,
      addLinkTemplateModal: false,
      addRulesTextModal: false,
      storyObjectToDelete: {},
      rules: {
        name: [
          {
            required: true,
            message: 'Name is required',
            trigger: 'blur',
          },
        ],
        sectionType: [
          {
            required: true,
            message: 'Please select section type',
            trigger: 'blue',
          },
        ],
        selectedViewscreens: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one viewscreen',
            trigger: 'change',
          },
        ],
        sections: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one section',
            trigger: 'change',
          },
        ],
        segments: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one segment',
            trigger: 'change',
          },
        ],
        apps: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one app',
            trigger: 'change',
          },
        ],
      },
      modalAppsData: null,
    }
  },
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isEditMode() {
      return this.$route.path.split('/').includes('edit')
    },
    selectedContent() {
      return this.content[this.selectedLanguage]
    },
    selectedStories() {
      return this.selectedContent.stories
    },
    selectedPreview() {
      return this.selectedContent.preview
    },
    selectedStoryItems() {
      return [{ ...this.selectedPreview, type: 'preview' }, ...this.selectedStories]
    },
    selectedStoryItem() {
      return this.selectedStoryItems.find(item => item.id === this.selectedTabId)
    },
    selectedStory() {
      return this.selectedStoryItemType === 'preview' ? null : this.selectedStoryItem
    },
    selectedRedirect() {
      return this.selectedStory?.redirect
    },
    selectedRules() {
      return this.selectedStory?.rules
    },
    selectedStoryIndex() {
      return this.selectedStories.findIndex(story => story.id === this.selectedTabId)
    },
    selectedStoryItemType() {
      return this.selectedStoryItem?.type !== undefined
        ? 'preview'
        : 'story'
    },
    selectedLanguageId() {
      return this.languages.find((language) => {
        return language.name === this.selectedLanguage
      }).id
    },
    storyId() {
      if (!this.isEditMode) {
        return null
      }
      return this.$route.params.id
    },
    storyUuid() {
      if (!this.storyToEdit) {
        return null
      }
      return this.storyToEdit.context.context.uuid
    },
    isDefaultContentFilled() {
      const defaultLang = this.content['ru']
      return (
        defaultLang?.preview?.url &&
        defaultLang.stories.every(story => story.preview)
      )
    },
    isViewscreen() {
      if (this.$route.params.viewscreenId || this.$route.params.sectionId) {
        return true
      }
      return false
    },
    breadcrumbs() {
      const defaultRootLink = '/media/stories'
      const lastSavedUrl = localStorage?.getItem('lastFilterListUrl')
      const rootLink = lastSavedUrl?.startsWith(defaultRootLink) ? lastSavedUrl : defaultRootLink

      return [
        { name: this.isViewscreen ? 'Main' : 'Media', link: this.isViewscreen ? `/main/${this.$route.params.viewscreenId}` : rootLink },
        { name: this.isEditMode ? 'Edit story' : 'New story', link: undefined },
      ]
    },
    isSectionTypeDisabled() {
      return !!this.form.sections.length || !!this.form.selectedViewscreens.length || !!this.form.segments.length
    },
    isRulesTypeDisabled() {
      return !!this.selectedRules.link.length // TODO: ADD QUILL
    },
    isLinkModalDisabled() {
      return !!this.selectedRedirect.link.length === 0
    },
    isAddStoryCoverDisabled() {
      return this.selectedStories.some(story => story.preview === '')
    },
    filteredLinkTemplates() {
      const uniqueTemplates = new Set()
      return this.linkTemplates.filter(template => {
        const url = template.url.trim()
        if (!url || uniqueTemplates.has(url)) {
          return false
        }
        uniqueTemplates.add(url)
        return true
      })
    },
    isInitialForm() {
      return isContentEqual(this.selectedContent, STORY_CONTENT_FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.languages.reduce((obj, { name }) => {
        obj[name] = !isContentEqual(this.content[name], STORY_CONTENT_FORM_FIELDS)
        return obj
      }, {})
    },
  },
  watch: {
    'form.selectedViewscreens'(viewscreenIds) {
      const allApps = viewscreenIds
        .map(id => {
          const vs = this.viewscreens.find(v => v.id === id)
          if (!vs) {
            console.warn('Viewsreen not found:', id, this.viewscreens)
          }
          return vs?.applications || []
        })
        .flat()
      const uniqueApps = new Map()
      allApps.forEach(app => {
        if (app?.application_id) {
          uniqueApps.set(app.application_id, app)
        }
      })
      this.form.apps = Array.from(uniqueApps.values())
      if (!this.form.selectedViewscreens.length) {
        this.form.sections = []
      }
      this.validateApps()
    },
    sectionType() {
      this.form.apps = []
    },
    selectedContent: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }

        this.$nextTick(() => this.validateForm())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }

      this.validateForm()
    },
  },
  async created() {
    this.mainLoading = true
    await this.getLocalizations()
    await this.getViewscreens()
    this.getSports()
    this.getLinkTemplates()
    this.getSegments()

    if (this.isViewscreen) {
      this.form.promoSections = [4]
    }

    if (this.$route.params.id) {
      this.storyToEdit = await this.getStory()
      this.fillForm()
    }

    if (!this.storyToEdit) {
      this.selectedLanguage = this.languages[0].name
      this.initializeContent()
    }
    this.selectedTabId = this.selectedStoryItems[0].id
    this.mainLoading = false
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    async validateStoryForm() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validate(resolve)
      })
      let atLeastOneValid = false
      let allAreValid = true
      let allRedirectsValid = true
      let allRedirectsColorValid = true
      let allLinkRulesValid = true
      let allDialogueRulesValid = true

      for (const lang in this.content) {
        const currentTab = this.content[lang]
        const hasValidPreview = currentTab.preview.url !== ''

        const validStories = currentTab.stories.filter(story => typeof story.id === 'number')

        const hasValidStory = validStories.length > 0

        if (hasValidPreview || hasValidStory) {
          atLeastOneValid = true
        }

        if ((hasValidPreview && !hasValidStory) || (!hasValidPreview && hasValidStory)) {
          allAreValid = false
        }

        validStories.forEach(story => {
          if (story.isRedirectAvailable) {
            const { buttonColor, buttonName, fontColor, linkType, sportId, link } = story.redirect
            const isMissingButton = !buttonName || !linkType || !fontColor || !buttonColor
            const isCustomMissing = linkType === 'custom' && (!link || link.length === 0)
            const isSportMissing = linkType === 'sport' && (sportId === undefined || sportId === null || sportId === '')
            const isLinkMissing = linkType !== 'sport' && (!link || link.length === 0)
            if (isMissingButton || isCustomMissing || isSportMissing || isLinkMissing) {
              allRedirectsValid = false
            }
            if (buttonColor.length !== 7 || fontColor.length !== 7) {
              allRedirectsColorValid = false
            }
          }

          if (story.isRulesAvailable) {
            const { type, linkType, sportId, link, rulesQuilText } = story.rules

            if (type === 'link') {
              const isMissingLinkType = !linkType
              const isCustomMissing = linkType === 'custom' && (!link || link.length === 0)
              const isSportMissing = linkType === 'sport' && (sportId === undefined || sportId === null || sportId === '')
              const isLinkMissing = linkType !== 'sport' && (!link || link.length === 0)

              if (isMissingLinkType || isCustomMissing || isSportMissing || isLinkMissing) {
                allLinkRulesValid = false
              }
            } else if (type === 'bottomDialogue') {
              const title = rulesQuilText?.title || ''
              const text = rulesQuilText?.text || ''

              if (!title.length || !text.length) {
                allDialogueRulesValid = false
              }
            }
          }
        })
      }

      const errors = {
        oneFullStory: !atLeastOneValid,
        bothPreviewAndStory: !allAreValid,
        isRedirectsValid: !allRedirectsValid,
        redirectColorLength: !allRedirectsColorValid,
        isLinkRuleValid: !allLinkRulesValid,
        isDialogueRuleValid: !allDialogueRulesValid,
      }

      this.errors = { ...errors }

      return isValid && !Object.values(this.errors).includes(true)
    },
    async validateSegments() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('segments', resolve)
      })
      return isValid
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('apps', resolve)
      })
      return isValid
    },
    async handleAddTemplatesModal(newTemplate) {
      this.addLinkTemplateModal = false
      this.linkTemplates.push(newTemplate)
    },
    async save() {
      this.loading = true

      const isPeriodsValidate = await this.$refs['activity-periods'].validate()

      const isFormValid = await this.validateStoryForm()
      if (!isFormValid || !isPeriodsValidate) {
        this.loading = false
        return
      }
      return await this.createStory()
    },
    async update() {
      this.loading = true
      const isPeriodsValidate = await this.$refs['activity-periods'].validate()

      const isFormValid = await this.validateStoryForm()

      if (!isFormValid || !isPeriodsValidate) {
        this.loading = false
        return
      }
      return await this.updateStory()
    },
    async deleteStory() {
      try {
        const storyResponse = await MediaService.deleteStory(this.storyId)

        if (!storyResponse || storyResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting story',
            description: storyResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Story deleted successfully',
        })
        if (this.isViewscreen) {
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting story',
          description: e.response.data.error.message,
        })
      }
    },
    async getStory() {
      try {
        this.loading = true
        const storyResponse = await MediaService.getStory(this.$route.params.id)
        this.loading = false
        return storyResponse.data.data
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.loading = false
      }
    },
    async getLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
        this.langOptions = this.languages.map((lang) => {
          return {
            label: lang.desc,
            value: lang.id,
          }
        })
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    async getLinkTemplates() {
      this.loading = true
      try {
        const response = await MediaService.getCustomLinks()
        this.linkTemplates = response.data.data
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.loading = false
      }
    },
    async getViewscreens() {
      this.loading = true
      try {
        const response = await ViewscreenService.getViewscreenTabs('', 'promo')
        if (response.data.data) {
          this.viewscreens = response.data.data.filter(
            (viewscreen) => !viewscreen.is_default)
        }
      } finally {
        this.loading = false
      }
    },
    async getSports() {
      this.loading = true
      try {
        const response = await MediaService.getSports()
        this.sports = response.data.data
      } catch (error) {
        console.log('Error while trying to get sports', error)
      } finally {
        this.loading = false
      }
    },
    async getSegments() {
      this.loading = true
      try {
        const response = await ViewscreenService.getSegmentatorSegments()
        if (response.data.data) {
          this.segments = response.data.data
        }
      } finally {
        this.loading = false
      }
    },
    getStoryCoversByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        const stories = content.stories.filter(story => typeof story.id === 'number' && !isNaN(story.id)).map((story) => {
          const { id, isRedirectAvailable, isRulesAvailable, redirect, rules } = story
          const storyObject = {
            id: id,
          }
          if (isRedirectAvailable) {
            const { buttonName, buttonColor, fontColor, linkType, linkName, sportId, link } = redirect
            storyObject.redirect = {
              type: 'link',
              link: {
                type: linkType,
                ...(link.length && { url: link || '' }),
                ...(linkType === 'custom' && { name: linkName }),
                ...(linkType === 'sport' && { sport_id: sportId }),
              },
            }

            const button = {
              ...(buttonName?.length && { name: buttonName }),
              ...(buttonColor?.length && { button_color: buttonColor }),
              ...(fontColor?.length && { font_color: fontColor }),
            }

            if (Object.keys(button).length) {
              storyObject.button = button
            }
          }
          if (isRulesAvailable) {
            const { type, linkType, linkName, sportId, link, iconType, rulesQuilText } = rules
            storyObject.rules = {
              icon_color: iconType,
            }
            if (type === 'link') {
              storyObject.rules.redirect = {
                type: 'link',
                link: {
                  type: linkType,
                  ...(link.length && { url: link || '' }),
                  ...(linkType === 'custom' && { name: linkName }),
                  ...(linkType === 'sport' && { sport_id: sportId }),
                },
              }
            } else {
              storyObject.rules.redirect = {
                type: 'dialogue',
                dialogue: {
                  title: rulesQuilText.title,
                  body: rulesQuilText.text,
                },
              }
            }
          }
          return storyObject
        })

        if (stories.length > 0) {
          acc[language] = stories
        }
        return acc
      }, {})
    },
    getPreviewsByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        if (typeof content.preview.id === 'number' && !isNaN(content.preview.id)) {
          acc[language] = content.preview.id
        }
        return acc
      }, {})
    },
    getApplications() {
      return this.form.apps.map((app) => {
        return {
          id: app.application_id,
          ...(app.min_app_version !== '' && { min_app_version: app.min_app_version }),
          ...(app.max_app_version !== '' && { max_app_version: app.max_app_version }),
        }
      })
    },
    getStoryPayload() {
      const storyPayload = {
        name: this.form.name,
        ...(this.form.extId?.trim() !== '') && { external_id: this.form.extId },
        ...(this.form.contentType !== undefined && { content_type: this.form.contentType }),
        periods: this.$refs['activity-periods'].getPeriods(),
        ...(this.form.sectionType === 'viewscreens' && { viewscreens: this.form.selectedViewscreens }),
        ...(this.form.sectionType === 'screens' && { applications: this.getApplications() }),
        ...(this.form.sectionType === 'screens' && { displays: this.form.sections }),
        ...(this.form.sectionType === 'screens' && { segments: this.form.segments }),
        is_active: this.form.is_active,
        images: this.getStoryCoversByLanguage(),
        previews: this.getPreviewsByLanguage(),
      }
      return storyPayload
    },
    async createStory() {
      try {
        this.mainLoading = true

        const storyPayload = this.getStoryPayload()
        const storyResponse = await MediaService.createStory(storyPayload)

        if (!storyResponse || storyResponse.success === false || storyResponse.data.error) {
          this.$notification.error({
            message: 'Error while creating story',
            description: storyResponse?.data.error.message || 'Unknown error',
          })
          this.loading = false
          this.mainLoading = false
          return
        }
        this.$notification.success({
          message: 'Story have been successfully created',
        })
        if (this.isViewscreen) {
          await this.addStoryToViewscreen(storyResponse.data.data.context.context.promoId)
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while creating story',
          description: e?.response?.data?.error?.message || '',
        })
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async updateStory() {
      try {
        this.mainLoading = true

        const storyPayload = this.getStoryPayload()
        const storyResponse = await MediaService.updateStory(this.storyId, storyPayload)
        if (!storyResponse || storyResponse.success === false || storyResponse.data.error) {
          this.$notification.error({
            message: 'Error while updating story',
            description: storyResponse?.data.error.message || 'Unknown error',
          })
          this.loading = false
          this.mainLoading = false
          return
        }
        this.$notification.success({
          message: 'Story have been successfully updated',
        })
        if (this.isViewscreen) {
          return this.redirectToViewscreen()
        }
        this.redirectToMedia()
      } catch (e) {
        this.$notification.error({
          message: 'Error while updating story',
          description: e?.response?.data?.error?.message || '',
        })
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async addStoryToViewscreen (promoId) {
      this.mainLoading = true
      try {
        const response = await ViewscreenService.addViewscreenSectionItem(this.$route.params.sectionId, promoId)
        if (response.data.data) {
          this.$notification.success({
            message: 'Story has been successfully added to the viewscreen section',
          })
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while adding story to viewscreen',
          description: e.response.data.error.message,
        })
      } finally {
        this.mainLoading = false
      }
    },
    openAddAppModal() {
      if (this.loading) {
        return
      }
      this.addAppsModal = true
    },
    closeAddAppsModal() {
      this.addAppsModal = false
    },
    openAddLinkTemplatesModal() {
      if (this.loading) {
        return
      }
      this.addLinkTemplateModal = true
    },
    handleTemplateChange(newTemplate, type) {
      const filtered = (newTemplate || []).filter(template => template !== '' && template !== undefined)
      const last = filtered[filtered.length - 1] || []
      const foundTemplate = this.linkTemplates.find(link => link.url === last)
      const templateName = foundTemplate ? foundTemplate.name : ''

      const target = type === 'redirect' ? this.selectedRedirect : this.selectedRules
      target.link = last
      target.linkName = templateName
    },

    handleLinkTypeChange(type, event) {
      if (event && event.key !== undefined) {
        if (event.key.length !== 1) return
      }
      const target = type === 'redirect'
        ? this.selectedRedirect
        : this.selectedRules
      Object.assign(target, { link: [], linkName: '', sportId: '' })
    },
    async deleteLinkTemplate(event, templateId, deletedlink) {
      try {
        const linkResponse = await MediaService.deleteLink(templateId)

        if (!linkResponse || linkResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting link',
            description: linkResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Link deleted successfully',
        })
        this.linkTemplates = this.linkTemplates.filter(link => link.id !== templateId)
        if (this.selectedLinkRedirect.link === deletedlink) {
          this.selectedLinkRedirect.link = []
        }
        if (this.selectedDialogueButton.link === deletedlink) {
          this.selectedDialogueButton.link = []
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting link',
          description: e.response.data.error.message,
        })
      }
      event.stopPropagation()
    },
    openRulesTextModal() {
      this.addRulesTextModal = true
    },
    closeRulesTextModal() {
      this.addRulesTextModal = false
    },
    handleSaveRules(newRules) {
      this.selectedRules.rulesQuilText = newRules
      this.closeRulesTextModal()
    },
    handleLangChange(selectedLangKey) {
      if (this.loading) {
        return
      }
      this.selectedLanguage = selectedLangKey
      this.selectedTabId = this.selectedStoryItems.find(item => item.type !== undefined)?.id
    },
    handleRedirectSwitch(checked) {
      if (!checked) {
        this.selectedStoryItem.redirect = deepClone(defaultStoryRedirect)
      }
    },
    handleRulesSwitch(checked) {
      if (!checked) {
        this.selectedStoryItem.rules = deepClone(defaultStoryRules)
      }
    },
    handleChangeRulesType() {
      const { iconType, type, ...restDefaults } = deepClone(defaultStoryRules)
      this.selectedStoryItem.rules = {
        ...restDefaults,
        type: this.selectedStoryItem.rules.type,
        iconType: this.selectedStoryItem.rules.iconType,
      }
    },
    handleTabChange(selectedId) {
      if (this.loading) {
        return
      }
      this.selectedTabId = selectedId
    },
    async validateUploadedStory(file) {
      const fileType = file.type.split('/')[0]
      const fileExtension = file.type.split('/')[1]
      const isImage = fileType === 'image'
      const isVideo = fileType === 'video'

      if (!isImage && !isVideo) {
        this.$notification.error({
          message: 'You can only upload image or video file!',
        })
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$notification.error({
          message: 'Story must be smaller than 10MB!',
        })
        return false
      }
      if (isVideo) {
        const { width, height } = await getVideoDimensions(file)
        if (width !== 1400 || height !== 2200) {
          this.$notification.error({
            message: 'Video must be 1400px x 2200px!',
          })
          return false
        }
        if (fileExtension !== 'mp4') {
          this.$notification.error({
            message: 'Video must have MP4 extension!',
          })
          return false
        }
      }

      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        if (width !== 1400 || height !== 2200) {
          this.$notification.error({
            message: 'Image must be 1400px x 2200px!',
          })
          return false
        }
        if (fileExtension !== 'png' && fileExtension !== 'webp' && fileExtension !== 'jpeg') {
          this.$notification.error({
            message: 'Image can be PNG, WEBP or JPG / JPEG extension!',
          })
          return false
        }
      }

      return file
    },
    async handleStoryUpload(event) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedStory(
        event.file.originFileObj,
      )

      if (!isFileValid) {
        return
      }

      const fileType = file.type.split('/')[0]
      const fileId = file.uid

      const story = {
        id: fileId,
        preview: '',
        loading: true,
        file,
      }

      try {
        const response = await MediaService.uploadImage(file)
        const storyId = response.data?.data?.id
        if (!storyId) {
          const errorMessage =
            response.data?.error?.message ||
            'Something went wrong while uploading the story'
          this.selectedContent.stories.splice(0, 1)
          this.$notification.error({
            message: errorMessage,
          })
          return
        }

        if (fileType === 'image') {
          story.id = storyId
          story.preview = await getBase64(event.file.originFileObj)
          story.loading = false
          story.isVideo = false
        }

        if (fileType === 'video') {
          story.id = storyId
          story.preview = response.data?.data?.path
          story.loading = false
          story.isVideo = true
        }

        this.$set(this.content[this.selectedLanguage].stories, this.selectedStoryIndex, {
          ...this.selectedStory,
          ...story,
        })

        this.selectedTabId = storyId
      } catch (e) {
        const errorMessage =
          e.response.data?.error?.message ||
          'Something went wrong while uploading the story'
        this.selectedContent.stories.splice(0, 1)
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    deleteStoryCover(index) {
      const deletedStoryId = this.selectedContent.stories[index].id
      this.selectedContent.stories.splice(index, 1)
      this.selectedContent.redirects = this.selectedContent.redirects.filter(redirect => redirect.storyId !== deletedStoryId)
    },
    async validateUploadedPreview(file) {
      const isImage = file.type.split('/')[0] === 'image'
      const fileExtension = file.type.split('/')[1]

      if (!isImage) {
        this.$notification.error({
          message: 'You can only upload image file!',
        })
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$notification.error({
          message: 'Preview must be smaller than 10MB!',
        })
        return false
      }

      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        if (width !== 500 || height !== 500) {
          this.$notification.error({
            message: 'Preview must be 500px x 500px!',
          })
          return false
        }
        if (fileExtension !== 'png' && fileExtension !== 'webp' && fileExtension !== 'jpeg') {
          this.$notification.error({
            message: 'Image can be PNG, WEBP or JPG / JPEG extension!',
          })
          return false
        }
      }

      return file
    },
    async handlePreviewUpload(event) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedPreview(
        event.file.originFileObj,
      )

      if (!isFileValid) {
        return
      }

      this.content[this.selectedLanguage].preview = {
        ...this.selectedPreview,
        loading: true,
        file,
      }

      try {
        const response = await MediaService.uploadImage(file)
        const previewId = response.data?.data?.id
        if (!previewId) {
          const errorMessage =
            response.data?.error?.message ||
            'Something went wrong while uploading the preview'
          this.content[this.selectedLanguage].preview = {
            ...defaultPreview,
          }
          this.$notification.error({
            message: errorMessage,
          })
        }
        this.content[this.selectedLanguage].preview.id = previewId
        this.content[this.selectedLanguage].preview.url = await getBase64(
          event.file.originFileObj,
        )
        this.content[this.selectedLanguage].preview.loading = false
        this.selectedTabId = previewId
      } catch (e) {
        const errorMessage =
          e.response.data?.error?.message ||
          'Something went wrong while uploading the preview'
        this.content[this.selectedLanguage].preview = {
          ...defaultPreview,
        }
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    addStoryCover() {
      const newStory = {
        ...deepClone(defaultCover),
        id: `new_${++this.tabCounter}`,
        name: `Story ${this.selectedStoryItems.length}`,
      }
      this.content[this.selectedLanguage].stories.push(newStory)
      this.selectedTabId = newStory.id
    },
    openDeleteCoverModal(storyItemId) {
      this.storyObjectToDelete = this.selectedStories.find(story => story.id === storyItemId)
      Modal.confirm({
        title: `Are you sure you want to delete ${this.storyObjectToDelete.name}`,
        content: 'You will not be able to recover the data',
        okText: 'Delete',
        cancelText: 'No',
        maskClosable: true,
        icon: 'exclamation-circle',
        onOk: () => {
          this.handleDeleteCover()
        },
      })
    },
    handleDeleteCover() {
      const deletedId = this.storyObjectToDelete.id
      const deletedIndex = this.content[this.selectedLanguage].stories.findIndex(story => story.id === deletedId)
      this.content[this.selectedLanguage].stories.splice(deletedIndex, 1)

      if (this.selectedTabId === deletedId) {
        this.selectedTabId = this.selectedStoryItems[0].id
      }

      this.content[this.selectedLanguage].stories.forEach((story, index) => {
        story.name = `Story ${index + 1}`
      })

      this.storyObjectToDelete = {}
    },
    handleCopyFromDefault() {
      if (!this.content[DEFAULT_LANG] || !this.content[this.selectedLanguage]) {
        return
      }

      const defaultForm = this.content[DEFAULT_LANG]

      this.content[this.selectedLanguage] = JSON.parse(
        JSON.stringify(defaultForm),
      )
      this.selectedTabId = this.selectedStoryItems[0].id
    },
    initializeContent() {
      this.content = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            preview: { ...defaultPreview, id: `new_${++this.tabCounter}` },
            stories: [{ ...deepClone(defaultCover), id: `new_${++this.tabCounter}` }],
          },
        }
      }, {})
    },

    fillForm() {
      const { name, external_id, content_type, segments, applications, displays, is_active, covers, previews } = this.storyToEdit.context.context
      this.form.name = name
      this.form.extId = external_id
      this.form.contentType = content_type
      this.form.apps = applications
      this.modalAppsData = applications
      if (this.storyToEdit.context.viewscreens.length) {
        this.form.selectedViewscreens = this.storyToEdit.context.viewscreens
      } else {
        this.form.sectionType = 'screens'
        this.form.segments = segments.map(segment => {
          return segment.id
        })
      }

      if (displays.length) {
        this.form.sections = displays.map((display) => {
          return display.id
        })
      }
      this.form.is_active = is_active

      const storyLanguages = Object.keys(previews)

      const storyContent = storyLanguages.reduce((acc, language) => {
        const storyContentData = {}

        storyContentData['stories'] = covers[language].map((image, index) => ({
          id: image.id,
          preview: image.path,
          loading: false,
          name: `Story ${index + 1}`,
          isVideo: image.info.mime === 'video/mp4',
          isRedirectAvailable: !!image.redirect,
          isRulesAvailable: !!image.rule,
          redirect: image.redirect
            ? {
              buttonName: image.button.name || '',
              buttonColor: image.button.button_color || '',
              fontColor: image.button.font_color || '',
              linkType: image.redirect.link.type || 'custom',
              linkName: image.redirect.link.name || '',
              sportId: image.redirect.link.sport_id || '',
              link: image.redirect.link.url || [],
            }
            : {
              buttonName: '',
              buttonColor: '',
              fontColor: '',
              linkType: 'custom',
              linkName: '',
              sportId: '',
              link: [],
            },
          rules: image.rule
            ? {
              type: image.rule.redirect.type === 'link' ? 'link' : 'bottomDialogue',
              linkType: image.rule.redirect.link.type || 'custom',
              linkName: image.rule.redirect.link.name || '',
              sportId: image.rule.redirect.link.sport?.sport_id || '',
              link: image.rule.redirect.link.url || [],
              iconType: image.rule.icon_color || 'dark',
              rulesQuilText: {
                title: image.rule.redirect.link?.title || '',
                text: image.rule.redirect.link?.body || '',
              },
            }
            : {
              type: 'link',
              linkType: 'custom',
              linkName: '',
              sportId: '',
              link: [],
              iconType: 'dark',
              rulesQuilText: {},
            },
        }))

        // Fill preview
        storyContentData['preview'] = {
          id: previews[language][0]?.id || '',
          url: previews[language][0]?.path || '',
          file: null,
          loading: false,
        }

        acc[language] = storyContentData
        return acc
      }, {})

      const defaultContent = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            preview: { ...defaultPreview, id: `new_${++this.tabCounter}` },
            stories: [{ ...deepClone(defaultCover), id: `new_${++this.tabCounter}` }],
          },
        }
      }, {})

      this.content = {
        ...defaultContent,
        ...storyContent,
      }
    },

    clearApps() {
      this.form.apps = []
    },
    changeSelectedApps(apps) {
      this.closeAddAppsModal()
      this.form.apps = apps
      this.modalAppsData = apps
      this.validateApps() // TODO : UPDATE
    },
    redirectToMedia() {
      returnToLastList(this.$router, '/media/stories')
    },
    setIconType(type) {
      this.selectedRules.iconType = type
    },
    redirectToViewscreen() {
      this.$router.push(`/main/${this.$route.params.viewscreenId}`)
    },
    redirectToRoute() {
      if (this.isViewscreen) {
        return this.redirectToViewscreen()
      }
      return this.redirectToMedia()
    },
    handleClearLang(lang) {
      const newPreviewId = `new_${++this.tabCounter}`
      this.content[lang] = {
        preview: { ...defaultPreview, id: newPreviewId },
        stories: [{ ...deepClone(defaultCover), id: `new_${++this.tabCounter}` }],
      }

      if (this.selectedLanguage === lang) {
        this.selectedTabId = newPreviewId
      }

      this.$refs.form.clearValidate()
    },
    validateForm() {
      if (!this.$refs.form) return

      this.$refs.form.validate((valid) => {
        this.validationResult = valid
      })
    },
  },
}
</script>

<style lang="scss" src="../../style.module.scss" />
